<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, shallowRef } from 'vue'
import { useDarkMode } from '../composables/useDarkMode'
import Chart from 'chart.js/auto'

const props = defineProps({
  monthlyExpenses: {
    type: Array,
    required: true
  },
  grossMonthlyIncome: {
    type: Number,
    required: true
  }
})

const { isDarkMode, currentTheme } = useDarkMode()
const chart = shallowRef(null)

function getChartColors() {
  const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement
  return [
    getComputedStyle(element).getPropertyValue('--chart-color-1').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-2').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-3').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-4').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-5').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-6').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-7').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-8').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-9').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-10').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-11').trim(),
    getComputedStyle(element).getPropertyValue('--chart-color-12').trim()
  ]
}

// Generate color variations by adjusting lightness
function generateColorVariations(baseColors, count) {
  const variations = []
  const baseCount = baseColors.length
  
  for (let i = 0; i < count; i++) {
    const baseColor = baseColors[i % baseCount]
    const cycle = Math.floor(i / baseCount)
    
    // For first cycle, use original colors
    if (cycle === 0) {
      variations.push(baseColor)
    } else {
      // For subsequent cycles, adjust lightness
      // Parse RGB color
      const rgb = baseColor.match(/\d+/g)
      if (rgb && rgb.length >= 3) {
        const [r, g, b] = rgb.map(Number)
        
        // Adjust brightness based on cycle (alternate lighter/darker)
        const factor = cycle % 2 === 1 ? 1.2 : 0.8
        const newR = Math.min(255, Math.floor(r * factor))
        const newG = Math.min(255, Math.floor(g * factor))
        const newB = Math.min(255, Math.floor(b * factor))
        
        variations.push(`rgb(${newR}, ${newG}, ${newB})`)
      } else {
        // Fallback to original color if parsing fails
        variations.push(baseColor)
      }
    }
  }
  
  return variations
}

// computed properties
const totalExpenses = computed(() =>
  props.monthlyExpenses.reduce((sum, e) => sum + e.amount, 0)
)

const remainingIncome = computed(() => 
  Math.max(0, props.grossMonthlyIncome - totalExpenses.value)
)

const chartData = computed(() => {
  // Force recalculation when theme changes by accessing currentTheme
  const _ = currentTheme.value
  const baseColors = getChartColors()
  
  // Sort expenses by amount (smallest to largest)
  const sortedExpenses = [...props.monthlyExpenses].sort((a, b) => a.amount - b.amount)
  
  // Generate enough color variations for all expenses
  const expenseColors = generateColorVariations(baseColors, sortedExpenses.length)
  
  // Build data with sorted expenses + remaining income
  const labels = [...sortedExpenses.map(e => e.name), 'Remaining Income']
  const data = [...sortedExpenses.map(e => e.amount), remainingIncome.value]
  
  // Use generated colors for expenses, then faint color for remaining income
  const backgroundColors = [
    ...expenseColors,
    'rgba(190, 190, 190, 0.15)' // Faint gray for remaining income to emphasize bills
  ]
  
  return {
    labels,
    datasets: [{
      data,
      backgroundColor: backgroundColors,
      borderColor: getCSSVariableValue('--light-card'),
      borderWidth: 2,
      hoverOffset: 15
    }]
  }
})

// chart creation + updating
const chartCanvas = ref(null)

function getCSSVariableValue(variableName) {
  // Read from body element since data-theme attribute is applied there
  const element = document.body.hasAttribute('data-theme') ? document.body : document.documentElement
  const value = getComputedStyle(element).getPropertyValue(variableName).trim()
  return value
}

function createChart() {
  console.log('go');
  if (!chartCanvas.value) {
    console.warn('Chart canvas not available')
    return
  }
  const ctx = chartCanvas.value.getContext('2d')

  // Get the computed colors from CSS variables
  const legendColor = getCSSVariableValue('--light-text')

  chart.value = new Chart(ctx, {
    type: 'doughnut',
    data: chartData.value,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '50%',
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            padding: 15,
            font: { size: 12 },
            color: legendColor,
            usePointStyle: true,
            pointStyle: 'circle',
            generateLabels: chart => {
              const data = chart.data
              if (data.labels.length && data.datasets.length) {
                return data.labels.map((label, i) => {
                  const value = data.datasets[0].data[i]
                  const percentage = ((value / props.grossMonthlyIncome) * 100).toFixed(1)
                  return {
                    text: `${label}: $${value.toFixed(2)} (${percentage}%)`,
                    fillStyle: data.datasets[0].backgroundColor[i],
                    strokeStyle: 'transparent',
                    fontColor: legendColor,
                    lineWidth: 0,
                    pointStyle: 'circle',
                    hidden: false,
                    index: i
                  }
                })
              }
              return []
            }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#fff',
          bodyColor: '#fff',
          borderColor: '#1364A3',
          borderWidth: 1,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: context => {
              const label = context.label || ''
              const value = context.parsed
              const percentage = ((value / props.grossMonthlyIncome) * 100).toFixed(1)
              return `${label}: $${value.toFixed(2)} (${percentage}%)`
            }
          }
        }
      }
    }
  })
}

function updateChartData() {
  if (!chart.value) {
    nextTick(createChart)
    return
  }
  
  // Update chart data without recreating
  chart.value.data = chartData.value
  chart.value.update('none')
}

function recreateChart() {
  // Destroy existing chart
  if (chart.value) {
    chart.value.destroy()
    chart.value = null
  }
  
  // Wait for DOM to update with new theme before recreating
  nextTick(createChart);
}

watch(() => props.monthlyExpenses, updateChartData, { deep: true })
watch(currentTheme, recreateChart)

onMounted(() => {
  if (props.monthlyExpenses.length > 0) createChart()
})

onBeforeUnmount(() => {
  if (chart.value) chart.value.destroy()
})
</script>

<template>
  <div class="monthly-expenses-card card h-100">
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-pie-chart"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Expense Breakdown</h3>
        <p class="card-subtitle">Monthly budget allocation</p>
      </div>
    </div>

    <div class="card-body mt-2">
      <div v-if="props.monthlyExpenses.length > 0" class="chart-container">
        <canvas ref="chartCanvas" width="400" height="500"></canvas>
      </div>

      <div v-else class="empty-state text-center py-4">
        <i class="bi bi-pie-chart display-4 text-muted mb-3"></i>
        <p class="text-muted mb-0">No expenses configured</p>
        <small class="text-muted">Add expenses to see the breakdown</small>
      </div>

      <div class="row mt-3">
        <div class="col-6">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Total Monthly Income
          </label>
          <div>
            <span class="stat-value income-value">{{ props.grossMonthlyIncome.toFixed(2) }}</span>
          </div>
        </div>
        <div class="col-6">
          <label class="form-label fw-semibold">
            <i class="fa-solid fa-receipt me-1"></i>Total Expenses
          </label>
          <div>
            <span class="stat-value expense-value">{{ totalExpenses.toFixed(2) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
